import { v4 as uuidv4 } from "uuid";

const STORAGE_KEY = "auth-shell-vwo-session-user-id";
const FME_SESSION_ID_KEY = "auth-shell-vwo-fme-session-id";

/** In-tab fallback when sessionStorage cannot read/write (disabled, quota, etc.) */
let memorySessionUserId: string | null = null;
let memoryFmeSessionId: number | null = null;

function tryGetItem(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function trySetItem(key: string, value: string): boolean {
  try {
    sessionStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

/**
 * Stable anonymous id for VWO bucketing for the lifetime of the browser tab.
 */
export function getOrCreateVwoSessionUserId(): string {
  if (typeof sessionStorage === "undefined") {
    return uuidv4();
  }
  let id = tryGetItem(STORAGE_KEY);
  if (id) {
    memorySessionUserId = null;
    return id;
  }
  id = memorySessionUserId ?? uuidv4();
  if (trySetItem(STORAGE_KEY, id)) {
    memorySessionUserId = null;
  } else {
    memorySessionUserId = id;
  }
  return id;
}

/**
 * Stable FME session id (unix seconds) for the browser tab, passed to `getFlag` as `sessionId`.
 * Matches SDK default type; without this, each evaluation can get a fresh timestamp.
 */
export function getOrCreateVwoFmeSessionId(): number {
  if (typeof sessionStorage === "undefined") {
    return Math.ceil(Date.now() / 1000);
  }
  const raw = tryGetItem(FME_SESSION_ID_KEY);
  if (raw) {
    const parsed = Number(raw);
    if (!Number.isNaN(parsed) && parsed > 0) {
      memoryFmeSessionId = null;
      return parsed;
    }
  }
  const sessionId = memoryFmeSessionId ?? Math.ceil(Date.now() / 1000);
  if (trySetItem(FME_SESSION_ID_KEY, String(sessionId))) {
    memoryFmeSessionId = null;
  } else {
    memoryFmeSessionId = sessionId;
  }
  return sessionId;
}
